import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Spinner } from '@nextui-org/react';
import { ArrowLeft, BarChart3, Calendar, Target, Trophy } from 'lucide-react';
import proService, {
  ProEvent,
  ProEventStatistics,
  ProLiveData,
  ProParticipant,
} from '../services/proService';
import { parseThrowByThrow } from '../services/throwByThrowParser';
import MatchScoreboard from '../components/spiele/MatchScoreboard';
import CommentSection from '../components/comments/CommentSection';

const ACCENT = '#3fced1';
const LIVE_REFRESH_INTERVAL_MS = 15_000;

// ============================================
// Helper
// ============================================

const getCountryName = (participant?: ProParticipant): string | null => {
  const country = participant?.country;
  if (!country) return null;
  if (typeof country === 'string') return country.trim() || null;
  if (typeof country === 'object' && typeof country.name === 'string') {
    return country.name.trim() || null;
  }
  return null;
};

/** Zieht eine Zahl aus Statistik-Werten wie "95.44", "40%", "12". */
const parseStatValue = (value?: string): number | null => {
  if (value === undefined || value === null) return null;
  const match = String(value).replace(',', '.').match(/-?\d+(\.\d+)?/);
  if (!match) return null;
  const num = parseFloat(match[0]);
  return isNaN(num) ? null : num;
};

// ============================================
// Seite
// ============================================

const MatchDetailPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();

  const [event, setEvent] = useState<ProEvent | null>(null);
  const [statistics, setStatistics] = useState<ProEventStatistics | null>(null);
  const [liveData, setLiveData] = useState<ProLiveData | null>(null);
  const [throwBlocks, setThrowBlocks] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const isMountedRef = useRef(true);

  const loadData = useCallback(
    async (silent: boolean) => {
      if (!eventId) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      if (!silent) setLoading(true);
      const loaded = await proService.getEventById(eventId);
      if (!isMountedRef.current) return;

      if (!loaded) {
        if (!silent) {
          setNotFound(true);
          setLoading(false);
        }
        return;
      }

      setEvent(loaded);
      setNotFound(false);

      // Statistiken + Scoreboard: bevorzugt frisch vom Endpoint, sonst embedded
      const showThrows = proService.isLive(loaded) || proService.isFinished(loaded);
      const [stats, live, blocks] = await Promise.all([
        proService.getEventStatistics(eventId),
        proService.isLive(loaded)
          ? proService.getEventLiveData(eventId)
          : Promise.resolve<ProLiveData | null>(null),
        showThrows
          ? proService.getEventThrowByThrowBlocks(eventId)
          : Promise.resolve<any[] | null>(null),
      ]);
      if (!isMountedRef.current) return;

      if (stats && Array.isArray(stats.statistics) && stats.statistics.length > 0) {
        setStatistics(stats);
      } else if (loaded.statistics) {
        setStatistics(loaded.statistics);
      } else {
        setStatistics(null);
      }
      setLiveData(live ?? loaded.liveData ?? null);
      // FlashLive liefert im Leg-Übergang sporadisch LEERE BLOCKS (30-90s) —
      // dann den letzten bekannten Stand behalten statt das Scoreboard zu leeren.
      if (blocks && blocks.length > 0) {
        setThrowBlocks(blocks);
      } else if (!silent) {
        setThrowBlocks(blocks ?? null);
      }
      if (!silent) setLoading(false);
    },
    [eventId]
  );

  useEffect(() => {
    isMountedRef.current = true;
    loadData(false);
    window.scrollTo({ top: 0 });
    return () => {
      isMountedRef.current = false;
    };
  }, [loadData]);

  // Auto-Refresh alle 15s, solange das Match live ist (silent)
  const isLive = proService.isLive(event ?? undefined);
  useEffect(() => {
    if (!isLive) return;
    const interval = window.setInterval(() => {
      loadData(true);
    }, LIVE_REFRESH_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [isLive, loadData]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  if (notFound || !event) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <Target className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Match nicht gefunden</h2>
          <p className="text-zinc-400 mb-6 text-sm">
            Das gesuchte Spiel existiert nicht oder ist nicht mehr verfügbar.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white text-sm inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück
          </button>
        </div>
      </div>
    );
  }

  const homeName = proService.getParticipantName(event.homeParticipant);
  const awayName = proService.getParticipantName(event.awayParticipant);
  const winner = proService.getEventWinner(event);
  const isFinished = proService.isFinished(event);
  const status = proService.getStatusBadge(event.status);
  const bestOf = event.matchFormat?.bestOf;
  const tournamentName = proService.getTournamentName(event.tournament);
  const timeLabel = proService.formatEventTime(event.startTime);
  const dateLabel = proService.formatEventDate(event.startTime);

  const homeScore =
    (isLive ? liveData?.homeScore?.current : undefined) ??
    proService.getHomeScore(event);
  const awayScore =
    (isLive ? liveData?.awayScore?.current : undefined) ??
    proService.getAwayScore(event);
  const showScores = isLive || isFinished;
  const serving = isLive ? liveData?.service : undefined; // 1 = home, 2 = away

  const statEntries = (statistics?.statistics ?? []).filter(
    (s) => s && (s.CATEGORY || '').trim()
  );

  const scoreboard = parseThrowByThrow(throwBlocks ?? [], isFinished);
  // Restpunkte des laufenden Legs aus liveData ergänzen, falls das Scoreboard
  // (noch) keine Wurf-Daten hat — so sind die Live-Scores trotzdem sichtbar.
  const liveRemaining =
    isLive &&
    !scoreboard.currentLeg &&
    (liveData?.homeRemainingScore != null || liveData?.awayRemainingScore != null)
      ? {
          home: liveData?.homeRemainingScore ?? null,
          away: liveData?.awayRemainingScore ?? null,
        }
      : null;

  const pageTitle = `${homeName} vs. ${awayName} | DartCloud`;
  const pageDescription = `${homeName} gegen ${awayName} – ${tournamentName}${
    event.round ? `, ${event.round}` : ''
  }. Live-Score, Statistiken und Match-Diskussion auf DartCloud.`;

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
      </Helmet>

      {/* Hero-Scoreboard */}
      <section className="border-b border-zinc-800 bg-gradient-to-b from-zinc-900 to-zinc-950">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-10">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück
          </button>

          {/* Turnier + Runde */}
          <div className="flex flex-col items-center text-center mb-6">
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Trophy size={14} color={ACCENT} className="shrink-0" />
              <span className="font-medium">{tournamentName}</span>
            </div>
            {event.round && (
              <span className="mt-1 text-xs uppercase tracking-widest text-zinc-500">
                {event.round}
              </span>
            )}
          </div>

          {/* Status-Zeile */}
          <div className="flex justify-center mb-8">
            {isLive ? (
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/15 border border-red-500/30">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                </span>
                <span className="text-xs font-bold text-red-400 tracking-widest">LIVE</span>
              </span>
            ) : isFinished ? (
              <span className="px-3 py-1 rounded-full bg-zinc-800 border border-zinc-700 text-xs font-semibold text-gray-300 tracking-wider uppercase">
                Endstand
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-zinc-800 border border-zinc-700 text-xs font-semibold text-gray-300">
                <Calendar size={12} className="shrink-0" />
                {dateLabel !== '-' ? `${dateLabel} · ${timeLabel} Uhr` : status.text}
              </span>
            )}
          </div>

          {/* Spieler + Score */}
          <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-3 sm:gap-8">
            <HeroPlayer
              participant={event.homeParticipant}
              name={homeName}
              isWinner={winner === 'home'}
              dimmed={winner === 'away'}
              serving={serving === 1}
            />

            <div className="flex flex-col items-center justify-center pt-6 sm:pt-8">
              {showScores ? (
                <div className="flex items-center gap-2 sm:gap-4">
                  <span
                    className={`text-5xl sm:text-7xl font-extrabold tabular-nums leading-none ${
                      winner === 'away' ? 'text-zinc-600' : ''
                    }`}
                    style={winner === 'home' ? { color: ACCENT } : undefined}
                  >
                    {homeScore ?? 0}
                  </span>
                  <span className="text-3xl sm:text-5xl font-light text-zinc-600 leading-none">
                    :
                  </span>
                  <span
                    className={`text-5xl sm:text-7xl font-extrabold tabular-nums leading-none ${
                      winner === 'home' ? 'text-zinc-600' : ''
                    }`}
                    style={winner === 'away' ? { color: ACCENT } : undefined}
                  >
                    {awayScore ?? 0}
                  </span>
                </div>
              ) : (
                <span className="text-4xl sm:text-6xl font-extrabold text-white tabular-nums leading-none">
                  {timeLabel !== '-' ? timeLabel : 'vs.'}
                </span>
              )}

              {bestOf ? (
                <span
                  className="mt-4 px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wider"
                  style={{ backgroundColor: `${ACCENT}1A`, color: ACCENT }}
                >
                  Best of {bestOf}
                </span>
              ) : null}
            </div>

            <HeroPlayer
              participant={event.awayParticipant}
              name={awayName}
              isWinner={winner === 'away'}
              dimmed={winner === 'home'}
              serving={serving === 2}
            />
          </div>

          {/* Live-Averages (falls vorhanden) */}
          {isLive && (liveData?.homeAverage || liveData?.awayAverage) && (
            <div className="mt-8 flex justify-center">
              <div className="flex items-center gap-6 rounded-xl border border-zinc-800 bg-zinc-900/60 px-5 py-2.5 text-sm tabular-nums">
                <span className="font-semibold text-white">
                  {liveData?.homeAverage ? `Ø ${liveData.homeAverage}` : '–'}
                </span>
                <span className="text-[11px] uppercase tracking-widest text-zinc-500">
                  3-Dart-Average
                </span>
                <span className="font-semibold text-white">
                  {liveData?.awayAverage ? `Ø ${liveData.awayAverage}` : '–'}
                </span>
              </div>
            </div>
          )}
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Scoreboard (Leg für Leg, throw-by-throw) */}
        {scoreboard.legs.length > 0 && (
          <section aria-label="Scoreboard" className="mb-10">
            <div className="flex items-center gap-2 mb-5">
              <Target className="w-5 h-5" style={{ color: ACCENT }} />
              <h2 className="text-lg font-semibold text-white">Scoreboard</h2>
              {isLive && (
                <span className="ml-1 inline-flex items-center gap-1.5 text-[10px] font-bold text-red-400 uppercase tracking-wider">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500" />
                  </span>
                  Live
                </span>
              )}
            </div>
            <MatchScoreboard
              scoreboard={scoreboard}
              homeName={homeName}
              awayName={awayName}
              isLive={isLive}
            />
          </section>
        )}

        {/* Fallback: Live-Restpunkte, wenn (noch) keine Wurf-Daten verfügbar sind */}
        {scoreboard.legs.length === 0 && liveRemaining && (
          <section aria-label="Aktuelles Leg" className="mb-10">
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-4 sm:p-6">
              <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-3">
                Aktuelles Leg – Restpunkte
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 px-4 py-3 text-center">
                  <p className="text-[11px] uppercase tracking-wider text-gray-500 truncate">
                    {homeName}
                  </p>
                  <p className="mt-1 text-3xl font-extrabold tabular-nums text-white">
                    {liveRemaining.home ?? '–'}
                  </p>
                </div>
                <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 px-4 py-3 text-center">
                  <p className="text-[11px] uppercase tracking-wider text-gray-500 truncate">
                    {awayName}
                  </p>
                  <p className="mt-1 text-3xl font-extrabold tabular-nums text-white">
                    {liveRemaining.away ?? '–'}
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Statistik-Vergleich */}
        {statEntries.length > 0 && (
          <section aria-label="Match-Statistiken" className="mb-10">
            <div className="flex items-center gap-2 mb-5">
              <BarChart3 className="w-5 h-5" style={{ color: ACCENT }} />
              <h2 className="text-lg font-semibold text-white">Statistiken</h2>
            </div>
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-4 sm:p-6">
              {/* Kopfzeile mit Spielernamen */}
              <div className="flex items-center justify-between mb-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                <span className="truncate">{homeName}</span>
                <span className="truncate text-right">{awayName}</span>
              </div>
              <div className="flex flex-col gap-5">
                {statEntries.map((entry, i) => (
                  <StatBar
                    key={`${entry.CATEGORY}-${i}`}
                    category={entry.CATEGORY || ''}
                    homeValue={entry.VALUE_HOME}
                    awayValue={entry.VALUE_AWAY}
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Match-Diskussion */}
        {eventId && (
          <CommentSection
            entityType="pro-event"
            entityId={event._id || eventId}
            title="Match-Diskussion"
          />
        )}
      </div>
    </div>
  );
};

// ============================================
// Hero-Spieler (großer Avatar mit Initialen-Fallback)
// ============================================

const HeroPlayer: React.FC<{
  participant?: ProParticipant;
  name: string;
  isWinner: boolean;
  dimmed: boolean;
  serving?: boolean;
}> = ({ participant, name, isWinner, dimmed, serving }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const image = proService.getParticipantImage(participant);
  const initials = proService.getParticipantInitials(participant);
  const country = getCountryName(participant);

  return (
    <div className="flex flex-col items-center text-center min-w-0">
      <div className="relative">
        <div
          className={`w-20 h-20 sm:w-28 sm:h-28 rounded-full bg-zinc-800 flex items-center justify-center overflow-hidden ${
            isWinner ? 'border-2' : 'border border-zinc-700'
          }`}
          style={isWinner ? { borderColor: ACCENT } : undefined}
        >
          {image && !imgFailed ? (
            <img
              src={image}
              alt={name}
              className="w-full h-full object-cover"
              onError={() => setImgFailed(true)}
            />
          ) : (
            <span className="text-xl sm:text-3xl font-bold text-gray-400 select-none">
              {initials}
            </span>
          )}
        </div>
        {isWinner && (
          <div
            className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center border-2 border-zinc-950"
            style={{ backgroundColor: ACCENT }}
          >
            <Trophy size={13} color="#000" />
          </div>
        )}
        {serving && (
          <span
            className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-zinc-950 animate-pulse"
            style={{ backgroundColor: ACCENT }}
            title="Am Wurf"
            aria-label="Am Wurf"
          />
        )}
      </div>
      <span
        className={`mt-3 text-base sm:text-xl font-bold leading-tight break-words ${
          dimmed ? 'text-zinc-500' : 'text-white'
        }`}
        style={isWinner ? { color: ACCENT } : undefined}
      >
        {name}
      </span>
      {country && (
        <span className="mt-1 text-[11px] uppercase tracking-widest text-zinc-500">
          {country}
        </span>
      )}
    </div>
  );
};

// ============================================
// Statistik-Balken (Vergleich Heim vs. Auswärts)
// ============================================

const StatBar: React.FC<{
  category: string;
  homeValue?: string;
  awayValue?: string;
}> = ({ category, homeValue, awayValue }) => {
  const home = parseStatValue(homeValue);
  const away = parseStatValue(awayValue);
  const total = (home ?? 0) + (away ?? 0);
  const homeShare = total > 0 ? Math.max(0, Math.min(100, ((home ?? 0) / total) * 100)) : 50;
  const awayShare = 100 - homeShare;
  const homeLeads = home !== null && away !== null && home > away;
  const awayLeads = home !== null && away !== null && away > home;

  const displayValue = (v?: string) => {
    const s = (v ?? '').trim();
    return s !== '' ? s : '–';
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-1.5">
        <span
          className={`text-sm font-bold tabular-nums ${
            awayLeads ? 'text-zinc-500' : 'text-white'
          }`}
          style={homeLeads ? { color: ACCENT } : undefined}
        >
          {displayValue(homeValue)}
        </span>
        <span className="text-xs text-gray-400 text-center truncate">{category}</span>
        <span
          className={`text-sm font-bold tabular-nums ${
            homeLeads ? 'text-zinc-500' : 'text-white'
          }`}
          style={awayLeads ? { color: ACCENT } : undefined}
        >
          {displayValue(awayValue)}
        </span>
      </div>
      <div className="flex items-center gap-1">
        <div className="flex-1 h-1.5 rounded-full bg-zinc-800 overflow-hidden flex justify-end">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${homeShare}%`,
              backgroundColor: homeLeads ? ACCENT : '#52525b',
            }}
          />
        </div>
        <div className="flex-1 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${awayShare}%`,
              backgroundColor: awayLeads ? ACCENT : '#52525b',
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default MatchDetailPage;
